/**
 * main.tsx — browser entry. Reads room/seat from the URL query, builds a
 * client-side engine purely for affordance highlighting (`validate` on the
 * redacted snapshot — no authority, D22), connects the reducer-only client to
 * the WS game server, and mounts `<App>`.
 *
 * Query params: `?room=<id>&playerId=<id>`, `?quick=1` for matchmaking, or
 * `?solo=1` for a room with the internal casual bot.
 * Without either mode it renders the lobby and opens no game socket.
 * Game links use same-origin `/ws` (wss under HTTPS); `ws=` is retained for
 * the separate Vite + manual-server development flow.
 */
import { lazy, StrictMode, Suspense, type ReactElement, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import '@fontsource-variable/jetbrains-mono';
// Design-system fonts (01-foundations §3.1). Both stylesheets split the face by
// unicode-range, so the browser fetches only the latin and cyrillic files.
import '@fontsource/press-start-2p/400.css';
import '@fontsource-variable/rubik/wght.css';
import type { Action, GameState, PlayerId } from '@cards/engine';
import { createEngine } from '@cards/engine';
import { registry } from '@cards/cards';
import type { SavedDeckSelectionRef } from '../server/protocol.js';
import { App, SessionCredentialErrorView } from './App.js';
import {
  AuthBootstrap,
  authAllowsAnonymousOrActiveApplication,
  authReturnToFromLocation,
  createBrowserLeanAuthHttpAdapter,
  createLeanAuthController,
  createOfficialGisButtonRenderer,
  type AuthRenderContext,
} from './auth/index.js';
import { createClient } from './client/index.js';
import { refreshIfClientBundleChanged } from './client/buildRefresh.js';
import { navigateTo, onNavigate } from './client/navigation.js';
import { loadBrowserProfile, type BrowserProfile } from './client/profile.js';
import {
  clearQuickSearchStarted,
  clearMatchDeckFlow,
  clearMatchDeckFlowSeats,
  clearMatchDeckIntent,
  copyMatchDeckFlow,
  matchDeckFlowCanStart,
  readMatchDeckIntent,
  readMatchDeckFlow,
  readMatchDeckMode,
  quickSearchStartedStorageKey,
  rememberMatchDeckIntent,
  rememberMatchDeckMode,
  resolveMatchDeckFlow,
  type MatchDeckFlow,
  type MatchDeckMode,
} from './decks/matchFlow.js';
import type { DeckReturnContext } from './decks/returnContext.js';
import type { SelectedDeckSummary } from './decks/LobbyDeckModal.js';
import { parseDeckPageRoute } from './decks/route.js';
import { createBrowserChallengeApi } from './challenges/http.js';
import {
  challengeMatchPath,
  parseChallengePageRoute,
  readChallengeMatchContext,
  storeChallengeReconnectCredential,
} from './challenges/route.js';
import {
  acknowledgeQuickSessionCredential,
  acknowledgeSoloSessionCredential,
  acknowledgeSessionCredential,
  clearQuickSessionCredential,
  clearSoloSessionCredential,
  clearSessionCredential,
  clientUrl,
  gameSeatFromSearch,
  isQuickMatchSearch,
  isSoloMatchSearch,
  prepareSessionCredential,
  prepareQuickSessionCredential,
  prepareSoloSessionCredential,
  promoteQuickSessionCredential,
  promoteSoloSessionCredential,
  quickClientUrl,
  soloClientUrl,
  type GameSeat,
} from './client/url.js';
import { fetchObservabilityConfig } from './observability/config.js';
import { initFrontendSentry } from './observability/sentry.js';
import { AppErrorBoundary } from './observability/AppErrorBoundary.js';
import { injectKitStyles } from './ui/kit/kit.css.js';
import { DelayedLoader, StatusScreen } from './ui/kit/index.js';
import { BetaTicker } from './ui/index.js';
import { Lobby } from './ui/Lobby.js';
import { ActivityNotificationsAdmin } from './admin/ActivityNotificationsAdmin.js';
import { AssistantProvider } from './assistant/index.js';
import {
  clearAutoStartPending,
  clearOnboardingSoloSeats,
  createBrowserOnboardingApi,
  type FastOnboardingProgress,
  isOnboardingSoloSeat,
  type OnboardingApi,
  OnboardingEntry,
  type OnboardingProjection,
  promoteOnboardingSoloSeat,
} from './onboarding/index.js';

// Общий UI-kit stylesheet, включая токены и Driver.js theme, монтируется один раз.
injectKitStyles();

// Fire-and-forget: install Sentry as soon as the runtime config arrives, without
// blocking first paint. The <AppErrorBoundary> below already catches render
// crashes regardless; once init resolves those reports reach Sentry too.
void fetchObservabilityConfig().then((config) => {
  if (config.sentry !== null) initFrontendSentry(config.sentry);
});

const Lab = lazy(async () => ({ default: (await import('./ui/Lab.js')).Lab }));
const Sandbox = lazy(async () => ({ default: (await import('./ui/Sandbox.js')).Sandbox }));
const DeckApplication = lazy(async () => ({
  default: (await import('./decks/DeckApplication.js')).DeckApplication,
}));
const ChallengeApplication = lazy(async () => ({
  default: (await import('./challenges/ChallengeApplication.js')).ChallengeApplication,
}));

const engine = createEngine(registry);
const canApply = (state: GameState, viewerId: PlayerId, action: Action): boolean =>
  engine.validate(state, action, viewerId).ok;

const rootEl = document.getElementById('root');
if (rootEl === null) throw new Error('main.tsx: #root не найден');
const root = createRoot(rootEl);
const authAdapter = createBrowserLeanAuthHttpAdapter();
const authController = createLeanAuthController(authAdapter);
const gisRenderer = createOfficialGisButtonRenderer();
const onboardingApi = createBrowserOnboardingApi();
const challengeApi = createBrowserChallengeApi();
const defaultTitle = document.title;

/**
 * Where the current URL points. Re-computed on every in-app navigation
 * (navigateTo/popstate) — screens swap in place instead of reloading the page.
 */
interface EntryRoute {
  seat: GameSeat | undefined;
  quickRequested: boolean;
  soloRequested: boolean;
  labRequested: boolean;
  sandboxRequested: boolean;
  deckRoute: ReturnType<typeof parseDeckPageRoute>;
  challengeRoute: ReturnType<typeof parseChallengePageRoute>;
  adminRequested: boolean;
}

function computeEntryRoute(): EntryRoute {
  const gamePageRequested = window.location.pathname === '/' && window.location.hash === '';
  const seat = gamePageRequested ? gameSeatFromSearch(window.location.search) : undefined;
  const quickRequested = gamePageRequested
    && seat === undefined
    && isQuickMatchSearch(window.location.search);
  const soloRequested = gamePageRequested
    && seat === undefined
    && !quickRequested
    && isSoloMatchSearch(window.location.search);
  const sandboxRequested = window.location.pathname === '/lab/sandbox' || window.location.pathname === '/lab/sandbox/';
  const labRequested = window.location.pathname === '/lab' || window.location.pathname === '/lab/';
  const deckRoute = window.location.search === ''
    && window.location.hash === ''
    ? parseDeckPageRoute(window.location.pathname)
    : undefined;
  const challengeRoute = window.location.search === '' && window.location.hash === ''
    ? parseChallengePageRoute(window.location.pathname)
    : undefined;
  const adminRequested = (window.location.pathname === '/admin' || window.location.pathname === '/admin/')
    && window.location.search === ''
    && window.location.hash === '';
  return {
    seat, quickRequested, soloRequested, labRequested, sandboxRequested, deckRoute, challengeRoute, adminRequested,
  };
}

/** Closes the previous screen's game socket before the next screen mounts. */
let teardownScreen: () => void = () => undefined;
let routeGeneration = 0;
let maintenanceScreenOpen = false;
let maintenancePoll: ReturnType<typeof setInterval> | undefined;

function MaintenanceScreen(): ReactElement {
  return (
    <StatusScreen
      variant="info"
      heading="Идут технические работы"
      message="Мы скоро вернёмся. Попробуйте обновить страницу немного позже."
    />
  );
}

function stopMaintenancePolling(): void {
  if (maintenancePoll !== undefined) globalThis.clearInterval(maintenancePoll);
  maintenancePoll = undefined;
}

function runtimeConfig(): Promise<{ readonly maintenanceEnabled?: boolean }> | undefined {
  const getRuntimeConfig = authAdapter.getRuntimeConfig;
  return typeof getRuntimeConfig === 'function'
    ? getRuntimeConfig.call(authAdapter)
    : undefined;
}

function renderMaintenanceScreen(): void {
  if (maintenanceScreenOpen) return;
  maintenanceScreenOpen = true;
  teardownScreen();
  teardownScreen = () => undefined;
  document.title = 'Технические работы';
  root.render(
    <StrictMode>
      <MaintenanceScreen />
      <BetaTicker />
    </StrictMode>,
  );
  maintenancePoll = globalThis.setInterval(() => {
    const request = runtimeConfig();
    if (request === undefined) return;
    void request.then((config) => {
      if (config.maintenanceEnabled === true || !maintenanceScreenOpen) return;
      stopMaintenancePolling();
      maintenanceScreenOpen = false;
      route();
    }).catch(() => undefined);
  }, 1_000);
}

/**
 * Onboarding progress survives screen swaps: without it every return to the
 * lobby (cancelled search, menu navigation) re-fetches the projection behind a
 * full-screen loader. The entry still refreshes it in the background.
 */
let onboardingProjectionCache: OnboardingProjection | undefined;

const onboardingCacheProps = () => ({
  ...(onboardingProjectionCache === undefined ? {} : { cachedProjection: onboardingProjectionCache }),
  onProjectionChange: (projection: OnboardingProjection) => {
    onboardingProjectionCache = projection;
  },
});

// The projection is account-owned: any auth transition (logout, future SPA
// login) must drop it so another account never sees a stranger's progress.
authController.subscribe(() => {
  onboardingProjectionCache = undefined;
});

function renderStorageError(error: unknown): void {
  const message = error instanceof Error ? error.message : 'Браузер не может сохранить ключ сессии.';
  // The auth shell only performs HTTP session checks, so a broken
  // local/session storage still renders the ordinary top menu around the error.
  renderWithAuth(() => <SessionCredentialErrorView message={message} />);
}

function effectiveProfile(profile: BrowserProfile, auth: AuthRenderContext): BrowserProfile {
  return auth.activeDisplayName === undefined
    ? profile
    : { ...profile, nickname: auth.activeDisplayName };
}

function effectiveProfileAtBootstrap(profile: BrowserProfile): BrowserProfile {
  const state = authController.getState();
  return state.kind === 'active'
    ? { ...profile, nickname: state.session.profile.displayName }
    : profile;
}

function matchPickerProps(
  flow: MatchDeckFlow,
  hasActiveAccount: boolean,
  returnContext: DeckReturnContext,
  preparedSelection?: SavedDeckSelectionRef,
  onPreparedApplied?: () => void,
  onChoiceRequired?: () => void,
): Readonly<{
  savedDeckPicker?: {
    returnContext: DeckReturnContext;
    preparedSelection?: SavedDeckSelectionRef;
    onPreparedApplied?: () => void;
    onChoiceRequired?: () => void;
  };
  savedDeckAccountRequired?: true;
}> | undefined {
  if (flow === 'preset') return undefined;
  if (!hasActiveAccount) return { savedDeckAccountRequired: true };
  return {
    savedDeckPicker: {
      returnContext,
      ...(preparedSelection === undefined ? {} : { preparedSelection }),
      ...(onPreparedApplied === undefined ? {} : { onPreparedApplied }),
      ...(onChoiceRequired === undefined ? {} : { onChoiceRequired }),
    },
  };
}

function returnContextForMatch(
  mode: MatchDeckMode,
  seat: GameSeat,
): DeckReturnContext | undefined {
  if (mode === 'quick' || mode === 'solo') return { version: 1, mode };
  if (mode === 'challenge') return undefined;
  return seat.playerId === 'A' || seat.playerId === 'B'
    ? { version: 1, mode: 'private', room: seat.room, playerId: seat.playerId }
    : undefined;
}

function renderWithAuth(
  children: (auth: AuthRenderContext) => ReactNode,
  nicknamePrefill = '',
): void {
  root.render(
    <StrictMode>
      <AppErrorBoundary>
        <AuthBootstrap
          controller={authController}
          adapter={authAdapter}
          gisRenderer={gisRenderer}
          returnTo={authReturnToFromLocation(window.location)}
          nicknamePrefill={nicknamePrefill}
        >
          {(auth) => (
            <AssistantProvider>
              {children(auth)}
            </AssistantProvider>
          )}
        </AuthBootstrap>
      </AppErrorBoundary>
      <BetaTicker />
    </StrictMode>,
  );
}

function renderLobby(profile?: BrowserProfile): void {
  try {
    const current = profile ?? loadBrowserProfile(window.localStorage, window.sessionStorage);
    renderWithAuth(
      (auth) => {
        // The lobby is account-only: deck selection and matchmaking both require
        // a signed-in profile. Anonymous visitors are prompted to sign in.
        if (auth.state.kind !== 'active') {
          return (
            <div className="cards-status" data-testid="lobby-account-required" role="status">
              Войдите в аккаунт, чтобы находить соперников и выбирать колоду.
            </div>
          );
        }
        const csrfToken = auth.state.session.csrfToken;
        const visibleProfile = effectiveProfile(current, auth);
        const startMatch = (mode: 'quick' | 'solo', deck: SelectedDeckSummary): void => {
          const room = mode === 'quick' ? '@quick' : '@solo';
          const ephemeralSeat = { playerId: current.presenceId };
          const gameSeat = { room, playerId: current.presenceId };
          if (mode === 'quick') clearQuickSessionCredential(window.sessionStorage, ephemeralSeat);
          else clearSoloSessionCredential(window.sessionStorage, ephemeralSeat);
          clearMatchDeckFlow(window.sessionStorage, gameSeat);
          if (mode === 'quick') clearQuickSearchStarted(window.sessionStorage, current.presenceId);
          else clearOnboardingSoloSeats(window.sessionStorage, gameSeat);
          // Freeze the chosen deck as the seat's pre-entry intent so the in-room
          // picker auto-applies it and no separate pre-match screen is needed.
          rememberMatchDeckMode(window.sessionStorage, gameSeat, mode);
          rememberMatchDeckIntent(window.sessionStorage, gameSeat, {
            deckId: deck.deckId,
            revision: deck.revision,
          });
          resolveMatchDeckFlow(window.sessionStorage, gameSeat, true);
          // In-place transition: the lobby stays mounted through the auth shell
          // and simply re-renders in its searching state — no page reload.
          navigateTo(mode === 'quick' ? '/?quick=1' : '/?solo=1');
        };
        const lobby = (onboarding?: {
          progress: FastOnboardingProgress;
          api: OnboardingApi;
          updateProgress: (progress: FastOnboardingProgress) => void;
        }) => (
          <Lobby
            key={visibleProfile.nickname}
            profile={visibleProfile}
            profileSource="account"
            csrfToken={csrfToken}
            {...(onboarding === undefined ? {} : {
              onboardingTour: {
                progress: onboarding.progress,
                csrfToken,
                api: onboarding.api,
                onCompleted: onboarding.updateProgress,
              },
            })}
            onFindOpponent={(deck) => startMatch('quick', deck)}
            onPlayBot={(deck) => startMatch('solo', deck)}
          />
        );
        return (
          <OnboardingEntry
            csrfToken={csrfToken}
            profile={visibleProfile}
            api={onboardingApi}
            {...onboardingCacheProps()}
          >
            {lobby}
          </OnboardingEntry>
        );
      },
      current.nickname,
    );
  } catch (error) {
    renderStorageError(error);
  }
}

function startApplication(entry: EntryRoute): void {
  const { seat, quickRequested, soloRequested } = entry;
  if (seat !== undefined) {
    try {
      // Mandatory write/read-back happens before createClient can open a socket.
      prepareSessionCredential(window.sessionStorage, seat);
      const entryAuth = authController.getState();
      const onboardingSoloEntry = entryAuth.kind === 'active'
        && isOnboardingSoloSeat(window.sessionStorage, seat);
      const matchMode = readMatchDeckMode(window.sessionStorage, seat) ?? 'private';
      rememberMatchDeckMode(window.sessionStorage, seat, matchMode);
      const challengeContext = matchMode === 'challenge'
        ? readChallengeMatchContext(window.sessionStorage, seat.room, seat.playerId)
        : undefined;
      // Challenge rooms already contain two exact server-owned deck snapshots.
      // They must never manufacture a client saved/preset flow or enter the
      // private-room deck picker path.
      const matchDeckFlow = matchMode === 'challenge'
        ? undefined
        : resolveMatchDeckFlow(
            window.sessionStorage,
            seat,
            entryAuth.kind === 'active',
          );
      const preparedSelection = readMatchDeckIntent(window.sessionStorage, seat);
      const hasActiveAccount = entryAuth.kind === 'active';
      const returnContext = returnContextForMatch(matchMode, seat);
      if (matchDeckFlow === 'saved' && returnContext === undefined) {
        throw new Error('Некорректное место private-комнаты для выбора своей колоды');
      }
      let presenceProfile: BrowserProfile | undefined;
      try {
        presenceProfile = loadBrowserProfile(window.localStorage, window.sessionStorage);
      } catch {
        // Presence is optional for private-link games and must never block them.
      }
      const client = createClient({
        url: () => clientUrl(
          window.location,
          seat,
          prepareSessionCredential(window.sessionStorage, seat),
        ),
        ...(presenceProfile?.nickname !== undefined && presenceProfile.nickname.length > 0
          ? { nickname: presenceProfile.nickname }
          : {}),
        beforeReconnect: refreshIfClientBundleChanged,
      });
      teardownScreen = () => client.transport.close();
      let handledAckCount = 0;
      let terminalCredentialCleared = false;
      let storageFailed = false;
      client.store.subscribe(() => {
        if (storageFailed) return;
        const state = client.store.getState();
        if ((state.sessionAckCount ?? 0) > handledAckCount && state.reconnectToken !== undefined) {
          handledAckCount = state.sessionAckCount!;
          try {
            acknowledgeSessionCredential(window.sessionStorage, seat, state.reconnectToken);
          } catch (error) {
            storageFailed = true;
            client.transport.close();
            client.store.notifyError(error instanceof Error ? error.message : 'Не удалось сохранить ключ сессии.');
            return;
          }
        }
        if (!terminalCredentialCleared && state.terminalCredentialRejection !== undefined) {
          terminalCredentialCleared = true;
          clearSessionCredential(window.sessionStorage, seat);
          clearMatchDeckFlowSeats(window.sessionStorage, seat);
        }
      });
      renderWithAuth(
        (auth) => {
          const visiblePresenceProfile = presenceProfile === undefined
            ? undefined
            : effectiveProfile(presenceProfile, auth);
          const challengeCsrfToken = auth.state.kind === 'active'
            ? auth.state.session.csrfToken
            : undefined;
          const retryChallenge = challengeContext === undefined || challengeCsrfToken === undefined
            ? undefined
            : async (): Promise<void> => {
                const session = await challengeApi.start(
                  challengeContext.trackId,
                  challengeContext.stage,
                  challengeCsrfToken,
                );
                storeChallengeReconnectCredential(window.sessionStorage, session, challengeContext);
                navigateTo(challengeMatchPath(session));
              };
          const saveChallengeDeck = challengeContext === undefined || challengeCsrfToken === undefined
            ? undefined
            : async (): Promise<Readonly<{ deckId: string }>> => {
                const result = await challengeApi.copyDeck(
                  challengeContext.trackId,
                  challengeContext.stage,
                  challengeCsrfToken,
                );
                return { deckId: result.deck.id };
              };
          return (
            <App
              store={client.store}
              canApply={canApply}
              fixedDeckMatch={matchMode === 'challenge'}
              {...(retryChallenge === undefined || saveChallengeDeck === undefined ? {} : {
                challengeMatch: {
                  onRetry: retryChallenge,
                  onSaveDeck: saveChallengeDeck,
                },
              })}
              {...(returnContext === undefined || matchDeckFlow === undefined
                ? {}
                : matchPickerProps(
                    matchDeckFlow,
                    hasActiveAccount,
                    returnContext,
                    preparedSelection,
                    () => clearMatchDeckIntent(window.sessionStorage, seat),
                    // The deck is chosen only on the home page, so an intent that
                    // cannot be applied sends the player back to it. Dropping the
                    // seat's intent AND flow keeps the return from bouncing
                    // straight back into this room.
                    () => {
                      clearMatchDeckIntent(window.sessionStorage, seat);
                      clearMatchDeckFlow(window.sessionStorage, seat);
                      navigateTo('/');
                    },
                  ))}
              {...(visiblePresenceProfile !== undefined ? { presenceProfile: visiblePresenceProfile } : {})}
              presenceProfileSource={auth.activeDisplayName === undefined ? 'local' : 'account'}
              {...(!onboardingSoloEntry || auth.state.kind !== 'active' ? {} : {
                onboardingGameTour: {
                  csrfToken: auth.state.session.csrfToken,
                  api: onboardingApi,
                  onCompleted: () => clearOnboardingSoloSeats(window.sessionStorage, seat),
                },
              })}
            />
          );
        },
        presenceProfile?.nickname ?? '',
      );
    } catch (error) {
      renderStorageError(error);
    }
  } else if (quickRequested || soloRequested) {
    try {
      const localProfile = loadBrowserProfile(window.localStorage, window.sessionStorage);
      const profile = effectiveProfileAtBootstrap(localProfile);
      if (profile.nickname.length === 0) {
        renderLobby(profile);
      } else {
      const ephemeralSeat = { playerId: profile.presenceId };
      const ephemeralGameSeat = {
        room: quickRequested ? '@quick' : '@solo',
        playerId: profile.presenceId,
      };
      const entryAuth = authController.getState();
      const onboardingSoloEntry = soloRequested
        && entryAuth.kind === 'active'
        && isOnboardingSoloSeat(window.sessionStorage, ephemeralGameSeat);
      if (onboardingSoloEntry) clearAutoStartPending(window.sessionStorage);
      rememberMatchDeckMode(window.sessionStorage, ephemeralGameSeat, quickRequested ? 'quick' : 'solo');
      const matchDeckFlow = resolveMatchDeckFlow(
        window.sessionStorage,
        ephemeralGameSeat,
        entryAuth.kind === 'active',
      );
      const preparedSelection = readMatchDeckIntent(window.sessionStorage, ephemeralGameSeat);
      const hasActiveAccount = entryAuth.kind === 'active';
      const returnContext: DeckReturnContext = quickRequested
        ? { version: 1, mode: 'quick' }
        : { version: 1, mode: 'solo' };
      const startedKey = quickSearchStartedStorageKey(profile.presenceId);
      const storedStartedAt = Number(window.sessionStorage.getItem(startedKey));
      const startedAt = Number.isSafeInteger(storedStartedAt) && storedStartedAt > 0
        ? storedStartedAt
        : Date.now();
      if (quickRequested) window.sessionStorage.setItem(startedKey, String(startedAt));
      if (quickRequested) prepareQuickSessionCredential(window.sessionStorage, ephemeralSeat);
      else prepareSoloSessionCredential(window.sessionStorage, ephemeralSeat);
      let activeGameSeat: GameSeat | undefined;
      const client = createClient({
        url: () => activeGameSeat === undefined
          ? quickRequested
            ? quickClientUrl(
                window.location,
                ephemeralSeat,
                prepareQuickSessionCredential(window.sessionStorage, ephemeralSeat),
              )
            : soloClientUrl(
                window.location,
                ephemeralSeat,
                prepareSoloSessionCredential(window.sessionStorage, ephemeralSeat),
              )
          : clientUrl(
              window.location,
              activeGameSeat,
              prepareSessionCredential(window.sessionStorage, activeGameSeat),
            ),
        nickname: profile.nickname,
        beforeReconnect: refreshIfClientBundleChanged,
      });
      teardownScreen = () => client.transport.close();
      let handledAckCount = 0;
      let terminalCredentialCleared = false;
      let storageFailed = false;
      client.store.subscribe(() => {
        if (storageFailed) return;
        const state = client.store.getState();
        if ((state.sessionAckCount ?? 0) > handledAckCount && state.reconnectToken !== undefined) {
          handledAckCount = state.sessionAckCount!;
          try {
            if (activeGameSeat === undefined) {
              if (quickRequested) {
                acknowledgeQuickSessionCredential(
                  window.sessionStorage,
                  ephemeralSeat,
                  state.reconnectToken,
                );
              } else {
                acknowledgeSoloSessionCredential(
                  window.sessionStorage,
                  ephemeralSeat,
                  state.reconnectToken,
                );
              }
            } else {
              acknowledgeSessionCredential(window.sessionStorage, activeGameSeat, state.reconnectToken);
            }
          } catch (error) {
            storageFailed = true;
            client.transport.close();
            client.store.notifyError(error instanceof Error ? error.message : 'Не удалось сохранить ключ сессии.');
            return;
          }
        }
        if (activeGameSeat === undefined
          && state.roomId !== undefined
          && state.viewerId !== undefined
          && state.roomState !== undefined
          && state.roomState.phase !== 'waiting_for_players') {
          try {
            const matchedSeat = { room: state.roomId, playerId: state.viewerId };
            if (quickRequested) {
              promoteQuickSessionCredential(window.sessionStorage, ephemeralSeat, matchedSeat);
            } else {
              promoteSoloSessionCredential(window.sessionStorage, ephemeralSeat, matchedSeat);
              if (onboardingSoloEntry) {
                promoteOnboardingSoloSeat(
                  window.sessionStorage,
                  ephemeralGameSeat,
                  matchedSeat,
                );
              }
            }
            copyMatchDeckFlow(window.sessionStorage, ephemeralGameSeat, matchedSeat);
            const url = new URL(window.location.href);
            url.searchParams.delete('quick');
            url.searchParams.delete('solo');
            url.searchParams.set('room', matchedSeat.room);
            url.searchParams.set('playerId', matchedSeat.playerId);
            window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
            activeGameSeat = matchedSeat;
            if (quickRequested) clearQuickSessionCredential(window.sessionStorage, ephemeralSeat);
            else clearSoloSessionCredential(window.sessionStorage, ephemeralSeat);
            clearMatchDeckFlow(window.sessionStorage, ephemeralGameSeat);
            window.sessionStorage.removeItem(startedKey);
          } catch (error) {
            storageFailed = true;
            client.transport.close();
            client.store.notifyError(error instanceof Error
              ? error.message
              : 'Не удалось сохранить адрес найденной игры.');
            return;
          }
        }
        if (!terminalCredentialCleared && state.terminalCredentialRejection !== undefined) {
          terminalCredentialCleared = true;
          if (quickRequested) clearQuickSessionCredential(window.sessionStorage, ephemeralSeat);
          else clearSoloSessionCredential(window.sessionStorage, ephemeralSeat);
          clearMatchDeckFlowSeats(window.sessionStorage, ephemeralGameSeat, activeGameSeat);
          if (quickRequested) clearQuickSearchStarted(window.sessionStorage, profile.presenceId);
          if (activeGameSeat !== undefined) {
            clearSessionCredential(window.sessionStorage, activeGameSeat);
          }
        }
      });
        renderWithAuth(
          (auth) => {
            const visibleProfile = effectiveProfile(localProfile, auth);
            return (
              <App
                key={auth.activeDisplayName ?? 'anonymous'}
                store={client.store}
                canApply={canApply}
                {...matchPickerProps(
                  matchDeckFlow,
                  hasActiveAccount,
                  returnContext,
                  preparedSelection,
                  () => {
                    clearMatchDeckIntent(window.sessionStorage, ephemeralGameSeat);
                    if (activeGameSeat !== undefined) {
                      clearMatchDeckIntent(window.sessionStorage, activeGameSeat);
                    }
                  },
                )}
                {...(quickRequested ? {
                  quickMatch: {
                    profile: visibleProfile,
                    profileSource: auth.activeDisplayName === undefined ? 'local' : 'account',
                    startedAt,
                    onCancel: () => {
                      client.transport.close();
                      clearQuickSessionCredential(window.sessionStorage, ephemeralSeat);
                      clearMatchDeckFlowSeats(window.sessionStorage, ephemeralGameSeat, activeGameSeat);
                      clearQuickSearchStarted(window.sessionStorage, profile.presenceId);
                      if (activeGameSeat !== undefined) {
                        clearSessionCredential(window.sessionStorage, activeGameSeat);
                      }
                      window.sessionStorage.removeItem(startedKey);
                      // Back to the idle lobby in place — the socket is already
                      // closed above, no page reload needed.
                      navigateTo('/');
                    },
                  },
                } : {
                  presenceProfile: visibleProfile,
                  presenceProfileSource: auth.activeDisplayName === undefined ? 'local' : 'account',
                })}
                {...(!onboardingSoloEntry || auth.state.kind !== 'active' ? {} : {
                  onboardingGameTour: {
                    csrfToken: auth.state.session.csrfToken,
                    api: onboardingApi,
                    onCompleted: () => clearOnboardingSoloSeats(
                      window.sessionStorage,
                      ephemeralGameSeat,
                      activeGameSeat,
                    ),
                  },
                })}
              />
            );
          },
          localProfile.nickname,
        );
      }
    } catch (error) {
      renderStorageError(error);
    }
  } else {
    renderLobby();
  }
}

function requestedMatchDeckSeat(entry: EntryRoute): GameSeat | undefined {
  if (entry.seat !== undefined) return entry.seat;
  if (!entry.quickRequested && !entry.soloRequested) return undefined;
  try {
    const profile = loadBrowserProfile(window.localStorage, window.sessionStorage);
    return {
      room: entry.quickRequested ? '@quick' : '@solo',
      playerId: profile.presenceId,
    };
  } catch {
    return undefined;
  }
}

function renderDeckOrLabRoute(entry: EntryRoute): void {
  const deckRoute = entry.deckRoute;
  document.title = deckRoute !== undefined
    ? deckRoute.kind === 'library' ? 'Мои колоды' : 'Редактор колоды'
    : entry.sandboxRequested ? 'Lab · Песочница' : 'Lab · Каталог карт';
  renderWithAuth(
    (auth) => {
      const sandboxScreen = auth.state.kind === 'active'
        ? <Sandbox csrfToken={auth.state.session.csrfToken} />
        : (
          <StatusScreen variant="info" heading="Песочница требует аккаунта">
            Персональная песочница сохраняется в аккаунт. Войдите, чтобы открыть её.
          </StatusScreen>
        );
      const application = (
        <Suspense fallback={<DelayedLoader />}>
          {deckRoute !== undefined
            ? <DeckApplication authState={auth.state} route={deckRoute} />
            : entry.sandboxRequested
              ? sandboxScreen
              : <Lab authenticated={auth.state.kind === 'active'} />}
        </Suspense>
      );
      if (auth.state.kind !== 'active') return application;
      const localProfile = loadBrowserProfile(window.localStorage, window.sessionStorage);
      return (
        <OnboardingEntry
          csrfToken={auth.state.session.csrfToken}
          profile={effectiveProfile(localProfile, auth)}
          api={onboardingApi}
          {...onboardingCacheProps()}
        >
          {() => application}
        </OnboardingEntry>
      );
    },
  );
}

function renderChallengeRoute(): void {
  document.title = 'Челленджи';
  renderWithAuth((auth) => (
    <Suspense fallback={<DelayedLoader />}>
      <ChallengeApplication authState={auth.state} />
    </Suspense>
  ));
}

function renderActivityAdminRoute(): void {
  document.title = 'Оператор · Уведомления';
  root.render(<ActivityNotificationsAdmin />);
}

function renderGameRoute(entry: EntryRoute): void {
  let applicationStarted = false;
  let cancelled = false;
  let stopWatching = (): void => undefined;
  let nicknamePrefill = '';
  try {
    nicknamePrefill = loadBrowserProfile(window.localStorage, window.sessionStorage).nickname;
  } catch {
    // The existing route bootstrap reports required browser-profile storage failures.
  }
  const startWhenAllowed = (): void => {
    const authState = authController.getState();
    if (applicationStarted || cancelled) return;
    if (!authAllowsAnonymousOrActiveApplication(authState)) return;
    const requestedSeat = requestedMatchDeckSeat(entry);
    if (requestedSeat === undefined) {
      applicationStarted = true;
      stopWatching();
      startApplication(entry);
      return;
    }
    const authenticated = authState.kind === 'active';
    const rememberedFlow = readMatchDeckFlow(window.sessionStorage, requestedSeat);
    const mode: MatchDeckMode = entry.quickRequested
      ? 'quick'
      : entry.soloRequested
        ? 'solo'
        : readMatchDeckMode(window.sessionStorage, requestedSeat) ?? 'private';

    // An account-bound seat opened without an active account session stays blocked.
    if (!matchDeckFlowCanStart(rememberedFlow, authenticated)) {
      renderWithAuth(
        () => (
          <div className="cards-status" data-testid="saved-deck-account-required" role="status">
            Эта игровая сессия привязана к аккаунту. Войдите, чтобы продолжить выбор колоды.
          </div>
        ),
        nicknamePrefill,
      );
      return;
    }

    // Deck selection lives ONLY on the lobby — there is no in-room picker to fall
    // back to. A fresh authenticated seat with no resolved deck flow (e.g. a
    // back-navigation to /?quick=1 after a match) returns to the lobby to choose.
    // Test-only private-invite seats (`?room=…`, reachable only when
    // TEST_ENABLE_LEGACY_PUBLIC_ROOM_CREATION is set under NODE_ENV=test) still
    // enter the room, where the seat sends them home if it has no intent to apply.
    if (authenticated && rememberedFlow === undefined && mode !== 'private' && mode !== 'challenge') {
      applicationStarted = true;
      stopWatching();
      // In-place redirect to the lobby: swap the URL and re-route without a reload.
      window.history.replaceState(null, '', '/');
      route();
      return;
    }

    applicationStarted = true;
    stopWatching();
    startApplication(entry);
  };
  const authState = authController.getState();
  if (authAllowsAnonymousOrActiveApplication(authState)) {
    // In-app navigation with auth already settled: mount the next screen
    // immediately — a blank intermediate render would flash the page.
    startWhenAllowed();
    return;
  }
  stopWatching = authController.subscribe(startWhenAllowed);
  // A later navigation must cancel this route's pending auth watcher AND the
  // bootstrap continuation below, or a stale closure could start a second
  // application over the new screen.
  teardownScreen = () => {
    cancelled = true;
    stopWatching();
  };
  renderWithAuth(() => null, nicknamePrefill);
  void authController.bootstrap().then(startWhenAllowed);
}

/**
 * Route dispatcher: renders the screen matching the current URL. Runs at page
 * load and again on every in-app navigation (navigateTo) or history traversal
 * (popstate), tearing down the previous screen's socket first.
 */
function route(): void {
  const currentRoute = ++routeGeneration;
  teardownScreen();
  teardownScreen = () => undefined;
  const entry = computeEntryRoute();
  if (entry.adminRequested) {
    maintenanceScreenOpen = false;
    stopMaintenancePolling();
    renderActivityAdminRoute();
    return;
  }
  const renderEntry = (): void => {
    if (currentRoute !== routeGeneration) return;
    maintenanceScreenOpen = false;
    stopMaintenancePolling();
    if (entry.challengeRoute !== undefined) {
      renderChallengeRoute();
      return;
    }
    if (entry.deckRoute !== undefined || entry.labRequested || entry.sandboxRequested) {
      renderDeckOrLabRoute(entry);
      return;
    }
    document.title = defaultTitle;
    renderGameRoute(entry);
  };
  const request = runtimeConfig();
  if (request === undefined) {
    renderEntry();
    return;
  }
  void request.then((config) => {
    if (currentRoute !== routeGeneration) return;
    if (config.maintenanceEnabled === true) {
      renderMaintenanceScreen();
      return;
    }
    renderEntry();
  }).catch(renderEntry);
}

onNavigate(route);
window.addEventListener('popstate', route);
route();
